import type { Node, NodeGroup } from '../types';

/**
 * Containment as a paint order.
 *
 * Konva draws in the order it is handed, so "inside" has to be spelled out as
 * "later". Two things on this canvas express containment, and they express it
 * differently:
 *
 *  - a frame owns its contents through `parentGroupId`, a link the editor
 *    maintains;
 *  - a drawing shape contains another simply by being drawn around it. Nobody
 *    records that. A box holding three smaller boxes is a box holding three
 *    smaller boxes only because of where they sit.
 *
 * Both have to cascade - a shape inside a shape inside a shape - so both are
 * measured rather than assumed, and the second is read straight off the
 * geometry the author can see.
 */

/** How many frames deep, walking up `parentGroupId`. */
export function nestingDepth(groupId: string | undefined, groups: NodeGroup[]): number {
    const byId = new Map(groups.map(g => [g.id, g]));
    const seen = new Set<string>();
    let cursor = groupId;
    let depth = 0;
    // A cycle is unreachable through the editor and perfectly reachable through
    // a hand-edited file; a render pass is the worst place to discover one.
    while (cursor && !seen.has(cursor)) {
        seen.add(cursor);
        cursor = byId.get(cursor)?.parentGroupId;
        depth++;
    }
    return depth;
}

/** A hair of slack, so a shape drawn flush against its container still counts as inside. */
const CONTAINMENT_SLACK = 0.5;

/** Whether `outer` is drawn around `inner`, entirely and with room to spare. */
export function encloses(outer: Node, inner: Node): boolean {
    const outerArea = outer.size.width * outer.size.height;
    const innerArea = inner.size.width * inner.size.height;
    // Strictly bigger, so two shapes drawn on top of each other at the same size
    // do not each claim to hold the other.
    if (outerArea <= innerArea) return false;
    return inner.position.x >= outer.position.x - CONTAINMENT_SLACK
        && inner.position.y >= outer.position.y - CONTAINMENT_SLACK
        && inner.position.x + inner.size.width <= outer.position.x + outer.size.width + CONTAINMENT_SLACK
        && inner.position.y + inner.size.height <= outer.position.y + outer.size.height + CONTAINMENT_SLACK;
}

/**
 * For each node, how many other nodes are drawn around it.
 *
 * Only full containment counts. Two shapes that merely overlap are left at the
 * same depth and keep the order the author stacked them in - a partial overlap
 * is a composition, not a nesting, and guessing at it would move things the
 * author put where they wanted them.
 */
export function containmentDepth(nodes: Node[]): Map<string, number> {
    // Biggest first, so the scan can stop as soon as the candidates are too
    // small to contain anything - containment implies a strictly larger area.
    const byArea = [...nodes].sort(
        (a, b) => b.size.width * b.size.height - a.size.width * a.size.height,
    );
    const depth = new Map<string, number>();
    for (let i = 0; i < byArea.length; i++) {
        let held = 0;
        for (let j = 0; j < i; j++) if (encloses(byArea[j], byArea[i])) held++;
        depth.set(byArea[i].id, held);
    }
    return depth;
}

/** Order frames by containment: whatever holds something else is drawn first. */
export function byNestingDepth<T>(
    groups: NodeGroup[],
    groupIdOf: (item: T) => string | undefined,
): (a: T, b: T) => number {
    const cache = new Map<string | undefined, number>();
    const depth = (id: string | undefined) => {
        let d = cache.get(id);
        if (d === undefined) { d = nestingDepth(id, groups); cache.set(id, d); }
        return d;
    };
    return (a, b) => depth(groupIdOf(a)) - depth(groupIdOf(b));
}

/**
 * Order nodes by containment, frames first and then geometry. Sorting is
 * stable, so anything at the same depth stays in the order it already had and
 * a diagram with nothing nested is left exactly as it was.
 */
export function byContainment(nodes: Node[], groups: NodeGroup[]): (a: Node, b: Node) => number {
    const frame = byNestingDepth<Node>(groups, node => node.parentGroupId);
    const held = containmentDepth(nodes);
    return (a, b) => frame(a, b) || (held.get(a.id) ?? 0) - (held.get(b.id) ?? 0);
}
